import React from "react";
import { createDrawerNavigator } from "@react-navigation/drawer";
import SettingsScreen from "./screens/settings/SettingsScreen";
import { DrawerParamList } from "./types/navigationParams";
import ListScreen from "./screens/list/ListScreen";
import EmployeesScreen from "./screens/employees/EmployeesScreen";
import BoardScreen from "./screens/section/BoardScreen";
import { useAppSelector } from "./redux/store";
import { useTranslation } from "react-i18next";
import { SCREEN_TYPES, USER_ROLES } from "./constants/constants";
import { Button, Icon } from "react-native-paper";
import JoinCompanyScreen from "./screens/all-companies/JoinCompanyScreen";
import { getReportsComponent } from "./utils/utils";
import { CustomDrawerContent } from "./CustomDrawerContent";
import { Screen } from "./config/companies/config";
import DriveScreen from "./screens/drive/DriveScreen";
import DeliveryParserScreen from "./screens/delivery/DeliveryParserScreen";
import RolePermissionsScreen from "./screens/settings/RolePermissionsScreen";

const Drawer = createDrawerNavigator<DrawerParamList>();

export default function DrawerNavigator() {
  const currentUser = useAppSelector((state) => state.auth.user);
  const config = useAppSelector((state) => state.auth.config);

  const { t } = useTranslation();

  const permittedScreens = config.screens.filter((screen: Screen) =>
    screen.roles?.includes(currentUser.role),
  );

  const listScreens = permittedScreens.filter(
    (screen: Screen) => screen.type === SCREEN_TYPES.LIST,
  );

  const boardScreens = permittedScreens.filter(
    (screen: Screen) => screen.type === SCREEN_TYPES.BOARD,
  );

  const reportsScreen = permittedScreens.filter(
    (screen: Screen) => screen.type === SCREEN_TYPES.REPORTS,
  );

  const driveScreens = permittedScreens.filter(
    (screen: Screen) => screen.type === SCREEN_TYPES.DRIVE,
  );

  const deliveryParserScreens = permittedScreens.filter(
    (screen: Screen) => screen.type === SCREEN_TYPES.DELIVERY_PARSER,
  );

  const requestedInitialScreen =
    config.initialScreenByRole?.[currentUser.role] ?? config.initialScreen;
  const initialRouteName = permittedScreens.some(
    (screen: Screen) => screen.name === requestedInitialScreen,
  )
    ? requestedInitialScreen
    : undefined;

  return (
    <Drawer.Navigator
      initialRouteName={initialRouteName as string | undefined}
      screenOptions={() => ({
        drawerType: "back",
        drawerStyle: { width: 300 },
      })}
      drawerContent={CustomDrawerContent}
    >
      {config.screens.length === 0 && (
        <Drawer.Screen
          name="JoinCompanyScreen"
          component={JoinCompanyScreen}
          options={{
            title: t("Join to company"),
            drawerIcon: ({ color, size }) => (
              <Icon
                source="cog-outline"
                color={color}
                size={size}
                testID="settingsScreen"
              />
            ),
          }}
        />
      )}
      {boardScreens.map((screen: Screen) => (
        <Drawer.Screen
          key={screen.name}
          name={screen.name as any}
          options={{
            title: t(screen.name),
            headerTitle: t(screen.name),
            drawerIcon: ({ size, color }) => (
              <Icon
                size={size}
                color={color}
                source="view-dashboard-variant-outline"
              />
            ),
          }}
          component={BoardScreen}
        />
      ))}
      {reportsScreen.map((screen: Screen) => {
        const Component = getReportsComponent(screen.name);

        if (!Component) return null;

        return (
          <Drawer.Screen
            key={screen.name}
            name={screen.name as any}
            component={Component}
            options={{
              title: t("Reports"),
              drawerIcon: ({ color, size }) => (
                <Icon
                  source="file-chart"
                  color={color}
                  size={size}
                  testID="reportsScreen"
                />
              ),
            }}
          />
        );
      })}
      {listScreens.map((screen: Screen) => (
        <Drawer.Screen
          key={screen.name}
          name={screen.name as any}
          component={ListScreen}
          options={{
            title: t(screen.name),
            drawerIcon: ({ color, size }) => (
              <Icon
                color={color}
                size={size}
                source={screen?.icon}
                testID={screen.name}
              />
            ),
          }}
        />
      ))}
      {deliveryParserScreens.map((screen: Screen) => (
        <Drawer.Screen
          key={screen.name}
          name={screen.name as any}
          component={DeliveryParserScreen}
          options={{
            title: t(screen.name),
            headerTitle: t(screen.name),
            drawerIcon: ({ color, size }) => (
              <Icon
                source={screen.icon || "text-box-plus-outline"}
                color={color}
                size={size}
                testID={screen.name}
              />
            ),
          }}
        />
      ))}
      {(currentUser.role === USER_ROLES.ADMIN ||
        config.employeesVisibleTo?.includes(currentUser.role)) && (
        <Drawer.Screen
          name="EmployeesScreen"
          component={EmployeesScreen}
          options={({ navigation }) => ({
            title: t("Users"),
            drawerIcon: ({ size, color }) => (
              <Icon
                size={size}
                color={color}
                source="account-group-outline"
                testID="EmployeesScreen"
              />
            ),
            headerRight: () => (
              <Button
                testID={"add-employee"}
                onPress={() => navigation.navigate("NewEmployeeForm" as never)}
              >
                {t("Add")}
              </Button>
            ),
          })}
        />
      )}
      {driveScreens.map((screen: Screen) => (
        <Drawer.Screen
          key={screen.name}
          name={screen.name as any}
          component={DriveScreen}
          options={{
            title: t("Files"),
            headerTitle: t("Files"),
            drawerIcon: ({ color, size }) => (
              <Icon
                source={screen.icon || "folder-outline"}
                color={color}
                size={size}
                testID="myDriveScreen"
              />
            ),
          }}
        />
      ))}
      {(currentUser.role === USER_ROLES.ADMIN ||
        currentUser.role === "SuperAdmin") && (
        <Drawer.Screen
          name="RolePermissions"
          component={RolePermissionsScreen}
          options={{
            title: t("Role permissions"),
            headerTitle: t("Role permissions"),
            drawerIcon: ({ color, size }) => (
              <Icon
                source="shield-account"
                color={color}
                size={size}
                testID="rolePermissionsScreen"
              />
            ),
          }}
        />
      )}
      <Drawer.Screen
        name="SettingsScreen"
        component={SettingsScreen}
        options={{
          title: t("Settings"),
          drawerIcon: ({ color, size }) => (
            <Icon
              source="cog-outline"
              color={color}
              size={size}
              testID="settingsScreen"
            />
          ),
        }}
      />
    </Drawer.Navigator>
  );
}
